import { createContext, use, useCallback, useEffect, useMemo, useState } from 'react';
import TagManager, { type TagManagerArgs } from 'react-gtm-module';
import { hotjar } from 'react-hotjar';

import { co2impactArticleDataByCountry, getRegionForPartner, getSiteForPartner } from '@sr/utils';
import { Country, type LearningCourse, type CountryCode } from '@sr/types';
import { checkIfInternalUser } from '#lib/utils/isInternalUser';
import { useProfileV2 } from '#lib/Profile';
import { useLocations } from '../../Locations/useLocations';
import { APP_ENV } from '../../utils/environment';
import { countries, useCountries } from '../useCountries';
import useHasCo2ImpactAccess from '../useHasCo2ImpactAccess';
import { useFlagsStatus } from '../useFlag';
import { type EVENT_CONSTANT, type EVENT_PAYLOAD } from './constants';
import {
  type LEARNING_PURCHASE_EVENT_PAYLOAD,
  type LEARNING_PURCHASE_EVENT_CONSTANT,
  resolveLearningProducts,
  learningProducts,
} from './learning';
import { type EVENT_ECOM_PRODUCT } from './base';

const GTM_DEV_ARGS = {
  gtmId: 'GTM-NDMGDHX',
};

const GTM_PROD_ARGS = {
  gtmId: 'GTM-NDMGDHX',
};

const articleDataSE = co2impactArticleDataByCountry(Country.SWEDEN);
const articleDataDK = co2impactArticleDataByCountry(Country.DENMARK);
const articleDataNO = co2impactArticleDataByCountry(Country.NORWAY);

export const SUBSCRIPTION_TYPE_CO2 = 'CO2'; // Do not change - shared and used for CO2 checkout request
export const SUBSCRIPTION_NAME_CO2_SE = articleDataSE.name;
export const SUBSCRIPTION_PRICE_CO2_SE = articleDataSE.pricePerMonth;
export const SUBSCRIPTION_CURRENCY_CO2_SE = articleDataSE.currency;
export const SUBSCRIPTION_NAME_CO2_DK = articleDataDK.name;
export const SUBSCRIPTION_PRICE_CO2_DK = articleDataDK.pricePerMonth;
export const SUBSCRIPTION_CURRENCY_CO2_DK = articleDataDK.currency;
export const SUBSCRIPTION_NAME_CO2_NO = articleDataNO.name;
export const SUBSCRIPTION_PRICE_CO2_NO = articleDataNO.pricePerMonth;
export const SUBSCRIPTION_CURRENCY_CO2_NO = articleDataNO.currency;
export const REQUEST_NEW_LOCATION_TYPE = 'requestNewLocation';
export const REQUEST_NEW_LOCATION_NAME = 'Request for new location';

const products: Record<string, EVENT_ECOM_PRODUCT> = {
  co2SE: {
    item_id: SUBSCRIPTION_TYPE_CO2,
    item_name: SUBSCRIPTION_NAME_CO2_SE,
    price: SUBSCRIPTION_PRICE_CO2_SE,
    currency: SUBSCRIPTION_CURRENCY_CO2_SE,
    quantity: 1,
  },
  co2DK: {
    item_id: SUBSCRIPTION_TYPE_CO2,
    item_name: SUBSCRIPTION_NAME_CO2_DK,
    price: SUBSCRIPTION_PRICE_CO2_DK,
    currency: SUBSCRIPTION_CURRENCY_CO2_DK,
    quantity: 1,
  },
  co2NO: {
    item_id: SUBSCRIPTION_TYPE_CO2,
    item_name: SUBSCRIPTION_NAME_CO2_NO,
    price: SUBSCRIPTION_PRICE_CO2_NO,
    currency: SUBSCRIPTION_CURRENCY_CO2_NO,
    quantity: 1,
  },
  requestNewLocation: {
    item_id: REQUEST_NEW_LOCATION_TYPE,
    item_name: REQUEST_NEW_LOCATION_NAME,
    price: 0,
    currency: 'SEK',
    quantity: 1,
  },
  requestBatteryPickup: {
    item_id: 'requestBatteryPickup',
    item_name: 'Request battery pickup',
    price: 0,
    currency: 'SEK',
    quantity: 1,
  },
  csrdPurchase: {
    item_id: 'csrdPurchase',
    item_name: 'CSRD Purchase',
    price: 0,
    currency: 'SEK',
    quantity: 1,
  },
  csrdPurchaseSE: {
    item_id: 'csrdPurchase',
    item_name: 'CSRD Purchase (SE)',
    price: 4900,
    currency: 'SEK',
    quantity: 1,
  },
  csrdPurchaseDK: {
    item_id: 'csrdPurchase',
    item_name: 'CSRD Purchase (DK)',
    price: 2500,
    currency: 'DKK',
    quantity: 1,
  },
  csrdPurchaseNO: {
    item_id: 'csrdPurchase',
    item_name: 'CSRD Purchase (NO)',
    price: 4900,
    currency: 'NOK',
    quantity: 1,
  },
  ...learningProducts,
} as const satisfies Record<string, EVENT_ECOM_PRODUCT>;

export type ProductKey = keyof typeof products;

type TrackEventFn = (
  event: EVENT_CONSTANT | LEARNING_PURCHASE_EVENT_CONSTANT,
  provider?: 'google' | 'hotjar',
  payload?: EVENT_PAYLOAD | LEARNING_PURCHASE_EVENT_PAYLOAD,
  noPrefix?: boolean,
) => void;

/**
 * Async version of trackEvent - only supports Google Analytics
 */
type TrackEventAsyncFn = ({
  event,
  payload,
}: {
  event: EVENT_CONSTANT | LEARNING_PURCHASE_EVENT_CONSTANT;
  payload?: EVENT_PAYLOAD | LEARNING_PURCHASE_EVENT_PAYLOAD;
}) => Promise<void>;
type TrackPageViewFn = (pageTitle: string) => void;
type TrackVirtualPageViewFn = (pageTitle: string, pagePath: string) => void;
type TrackFileDownloadFn = (filename: string, filetype: FileType) => void;
type TrackProductViewFn = (productKey: ProductKey) => void;
type TrackBeginCheckoutFn = (productKey: ProductKey) => void;
type TrackLearningPurchaseFn = (
  course: LearningCourse,
  event: LEARNING_PURCHASE_EVENT_CONSTANT,
  payload: LEARNING_PURCHASE_EVENT_PAYLOAD,
) => void;
type TrackPurchaseFn = (productKey: ProductKey, transactionId: string) => void;

export const GOOGLE_ECOM_PRODUCTS: Record<keyof typeof products, EVENT_ECOM_PRODUCT> = products;

type DataLayerItem = {
  [key: string]: string | number | DataLayerItem | ((e: unknown) => void);
};

export type FileType = 'pdf' | 'xlsx' | 'csv' | 'png' | 'zip';

declare global {
  interface Window {
    dataLayer?: DataLayerItem[];
  }
}

type EventTrackingInterface = {
  trackEvent: TrackEventFn; // function that tracks events
  trackEventAsync: TrackEventAsyncFn; // async function that tracks events
  trackPageView: TrackPageViewFn;
  trackProductView: TrackProductViewFn;
  trackBeginCheckout: TrackBeginCheckoutFn;
  trackPurchase: TrackPurchaseFn;
  trackLearningPurchase: TrackLearningPurchaseFn;
  trackFileDownload: TrackFileDownloadFn;
  trackPrint: VoidFunction;
  trackVirtualPageView: TrackVirtualPageViewFn;
};

type EventTrackingProps = {
  children: React.ReactNode;
};

const defaultContextInterface: EventTrackingInterface = {
  trackEvent: () => null,
  trackEventAsync: () => Promise.resolve(),
  trackPageView: () => null,
  trackProductView: () => null,
  trackBeginCheckout: () => null,
  trackPurchase: () => null,
  trackLearningPurchase: () => null,
  trackFileDownload: () => null,
  trackPrint: () => null,
  trackVirtualPageView: () => null,
};

const EventTracking = createContext<EventTrackingInterface>(defaultContextInterface);

const EventTrackingProvider = ({ children }: EventTrackingProps) => {
  const { flagsReady } = useFlagsStatus();
  const [hotjarReady, setHotjarReady] = useState(false);
  const [dataLayer, setDataLayer] = useState<DataLayerItem[]>([]);
  const [userLang, setUserLang] = useState<string | undefined>(undefined);
  const [userId, setUserId] = useState<string | undefined>(undefined);
  const [userLevel, setUserLevel] = useState<string | undefined>(undefined);
  const [userType, setUserType] = useState<string | undefined>(undefined);
  const [persona, setPersona] = useState<string | undefined>(undefined);
  const [marketCodes, setMarketCodes] = useState<CountryCode[]>([]);
  const [orgIds, setOrgIds] = useState<string | undefined>(undefined);
  const [subscriptionTypes, setSubscriptionTypes] = useState<string | undefined>(undefined);
  const [globalPageTitle, setGlobalPageTitle] = useState<string>('');
  const [trackVirtualPageViewState, setTrackVirtualPageViewState] = useState({
    pageTitle: '',
    pagePath: '',
  });

  const { allDwKeys, locationsById, loading: loadingLocations } = useLocations();

  const { regions, sites } = useMemo(() => {
    if (loadingLocations || !allDwKeys.length) return { regions: null, sites: null };

    const regionsForUser = new Set<string>();
    const sitesForUser = new Set<string>();

    allDwKeys.forEach((dwKey) => {
      const partner = locationsById[dwKey];

      if (!partner) return;

      const site = getSiteForPartner(partner);
      const region = getRegionForPartner(partner);

      if (site) sitesForUser.add(site.name);
      if (region) regionsForUser.add(region);
    });

    return {
      regions: Array.from(regionsForUser),
      sites: Array.from(sitesForUser),
    };
  }, [loadingLocations, allDwKeys, locationsById]);

  const tagManagerArgs = useMemo<TagManagerArgs>(
    () => (APP_ENV === 'production' ? GTM_PROD_ARGS : GTM_DEV_ARGS),
    [],
  );

  const [GTMInitiated, setGTMInitiated] = useState(false);

  const { profile } = useProfileV2();
  const { supportedCountryCodes } = useCountries();
  const hasCo2ImpactPlus = useHasCo2ImpactAccess();

  useEffect(() => {
    if (userLang) {
      dataLayer.push({
        event: 'site_info',
        siteInfo: { languageCode: userLang },
      });
    }
  }, [dataLayer, userLang]);

  useEffect(() => {
    if (profile?.language && userLang !== profile.language) {
      setUserLang(profile.language);
    }
  }, [profile, setUserLang, userLang]);

  useEffect(() => {
    if (GTMInitiated) {
      setDataLayer(window.dataLayer ?? []);
    }
  }, [GTMInitiated]);

  useEffect(() => {
    if (!GTMInitiated) {
      TagManager.initialize({
        ...tagManagerArgs,
      });
      setGTMInitiated(true); // we need to make sure tagManager is initialized before we assign the DataLayer
    }
  }, [GTMInitiated, tagManagerArgs]);

  useEffect(() => {
    if (flagsReady && !hotjarReady && APP_ENV === 'production') {
      const hotjarId = 1249513;

      hotjar.initialize({
        id: hotjarId,
        sv: 6,
      });
      setHotjarReady(true);
    }
  }, [flagsReady, hotjarReady]);

  useEffect(() => {
    if (!profile) {
      return;
    }

    const roles = profile.roles.join(', ');
    const industry = profile.industry ?? '';

    if (!hotjar.initialized()) {
      return;
    }
    if (userType && userLevel && hotjarReady && marketCodes.length > 0 && !!sites) {
      const markets = countries
        .filter((country) => marketCodes.includes(country.code))
        .map((country) => country.name);

      hotjar.identify(profile.email ?? profile.userId, {
        marketCodes: markets.toSorted((a, b) => a.localeCompare(b)).toString(),
        userType,
        sites: sites.join(', '),
        regions: regions.join(', '),
        hasCo2ImpactPlus,
        userLevel,
        roles,
        industry,
      });
    }
  }, [profile, marketCodes, userType, userLevel, hotjarReady, hasCo2ImpactPlus, sites, regions]);

  const trackEventFn: TrackEventFn = useCallback(
    (event, provider, payload, noPrefix?: boolean) => {
      const eventname = !noPrefix ? 'csp_' + event : event;

      switch (provider) {
        case 'google':
          // @ts-expect-error this ought to be allowed or I shall eat meine hat
          dataLayer.push({ event: eventname, ...(payload ?? {}) });
          break;
        case 'hotjar':
          if (hotjar.initialized()) {
            hotjar.event(event);
          }
          break;
        default:
          dataLayer.push({
            event: eventname,
          });
          if (hotjar.initialized()) {
            hotjar.event(event);
          }
      }
    },
    [dataLayer],
  );

  const trackEventAsync: TrackEventAsyncFn = useCallback(
    ({ event, payload }) => {
      const eventname = 'csp_' + event;

      return new Promise((resolve) => {
        // @ts-expect-error this ought to be allowed or I shall eat meine hat
        dataLayer.push({
          event: eventname,
          ...(payload ?? {}),
          eventCallback: () => {
            resolve();
          },
        });
      });
    },
    [dataLayer],
  );

  // Exposed method to track pageviews/page title changes
  const trackPageViewFn: TrackPageViewFn = useCallback(
    (globalTitle: string) => {
      if (globalTitle !== globalPageTitle) {
        setGlobalPageTitle(globalTitle); // memoize page title for rendering consistency
      }
    },
    [globalPageTitle],
  );

  const trackVirtualPageViewFn: TrackVirtualPageViewFn = useCallback(
    (pageTitle: string, pagePath: string) => {
      if (
        pageTitle !== trackVirtualPageViewState.pageTitle ||
        pagePath !== trackVirtualPageViewState.pagePath
      ) {
        trackPageViewFn(pageTitle); // adding pageview for testing purposes
        trackEventFn(
          'page_view',
          'google',
          {
            page_title: pageTitle,
            page_location: window.location.origin + pagePath,
          },
          true,
        );
        setTrackVirtualPageViewState({ pageTitle, pagePath });
      }
    },
    [
      trackEventFn,
      trackPageViewFn,
      trackVirtualPageViewState.pagePath,
      trackVirtualPageViewState.pageTitle,
    ],
  );

  // Exposed method to track product view
  const trackProductViewFn: TrackProductViewFn = useCallback(
    (productKey) => {
      const event = 'view_item';
      const payload: EVENT_PAYLOAD = {
        ecommerce: {
          items: [GOOGLE_ECOM_PRODUCTS[productKey]],
        },
      };

      trackEventFn(event, 'google', payload, true);
    },
    [trackEventFn],
  );

  // Exposed method to track begin checkout
  const trackBeginCheckoutFn: TrackBeginCheckoutFn = useCallback(
    (productKey) => {
      const event = 'begin_checkout';
      const payload: EVENT_PAYLOAD = {
        ecommerce: {
          items: [GOOGLE_ECOM_PRODUCTS[productKey]],
        },
      };

      trackEventFn(event, 'google', payload, true);
    },
    [trackEventFn],
  );

  // Exposed method to track learning purchase events
  const trackLearningPurchaseFn: TrackLearningPurchaseFn = useCallback(
    (course, event, payload) => {
      // Always emit the custom event so analytics data is never silently dropped (i.e 'learning_checkout_next_clicked')
      trackEventFn(event, 'google', payload);

      const learningProductsForCourse = resolveLearningProducts(
        course,
        payload.single_quantity,
        payload.bundle_quantity,
      );

      if (learningProductsForCourse.length === 0) {
        // Course is not yet mapped to GA products; skip ecommerce events
        return;
      }

      switch (event) {
        case 'learning_checkout_started':
          trackEventFn(
            'begin_checkout',
            'google',
            { ecommerce: { items: learningProductsForCourse } },
            true,
          );
          break;
        case 'learning_order_confirmed': {
          trackEventFn(
            'purchase',
            'google',
            {
              ecommerce: {
                transaction_id: payload.purchase_id,
                currency: learningProductsForCourse[0].currency,
                value: learningProductsForCourse.reduce(
                  (total, item) => total + item.price * item.quantity,
                  0,
                ),
                items: learningProductsForCourse,
              },
            },
            true,
          );
          break;
        }
        default:
          break;
      }
    },
    [trackEventFn],
  );

  // Exposed method to track completed purchase
  const trackPurchaseFn: TrackPurchaseFn = useCallback(
    (productKey, transactionId) => {
      const event = 'purchase';
      const payload: EVENT_PAYLOAD = {
        ecommerce: {
          transaction_id: transactionId,
          currency: GOOGLE_ECOM_PRODUCTS[productKey].currency,
          value: GOOGLE_ECOM_PRODUCTS[productKey].price,
          items: [GOOGLE_ECOM_PRODUCTS[productKey]],
        },
      };

      trackEventFn(event, 'google', payload, true);
    },
    [trackEventFn],
  );

  useEffect(() => {
    if (globalPageTitle !== '') {
      // trigger only on local state change (for memoization)
      dataLayer.push({
        event: 'page_info',
        pageInfo: { globalPageTitle },
      });
    }
  }, [dataLayer, globalPageTitle]);

  //Track supported markets
  useEffect(() => {
    if (supportedCountryCodes.length !== 0) {
      setMarketCodes(supportedCountryCodes);
    }
  }, [supportedCountryCodes]);

  // Track userId
  useEffect(() => {
    if (profile?.userId) {
      setUserId(profile.userId);
    }
  }, [profile?.userId]);

  // Track orgId
  useEffect(() => {
    if (profile?.permissions?.businessPartners) {
      setOrgIds(profile.permissions.businessPartners.map((bp) => bp.id).toString());
    }
  }, [profile?.permissions?.businessPartners]);

  // Track subscription types
  useEffect(() => {
    const types: string[] = [];

    if (hasCo2ImpactPlus) {
      types.push('CO2 Impact');
    }

    setSubscriptionTypes(types.toString());
  }, [hasCo2ImpactPlus]);

  // Track user level
  useEffect(() => {
    if (profile?.permissions?.superUser) {
      setUserLevel('superuser');
    } else {
      setUserLevel('user');
    }
  }, [profile]);

  // Track user type
  useEffect(() => {
    if (checkIfInternalUser(profile?.email)) {
      setUserType('internal');
    } else {
      setUserType('external');
    }
  }, [profile]);

  // Track persona
  useEffect(() => {
    setPersona('user');
  }, []);

  //Push userInfo event to datalayer
  useEffect(() => {
    if (
      profile &&
      userId &&
      marketCodes.length > 0 &&
      userLevel &&
      userType &&
      persona &&
      regions
    ) {
      dataLayer.push({
        event: 'user_info',
        userInfo: {
          marketCodes: marketCodes.toSorted((a, b) => a.localeCompare(b)).toString(),
          userID: userId,
          orgID: orgIds ?? '',
          subscriptionTypes: subscriptionTypes ?? '',
          userType,
          userLevel,
          persona,
          regions: regions.join(', '),
          roles: profile.roles.join(', '),
          industry: profile.industry ?? '',
        },
      });
    }
  }, [
    dataLayer,
    marketCodes,
    userId,
    orgIds,
    subscriptionTypes,
    userLevel,
    userType,
    persona,
    regions,
    profile,
  ]);

  // Exposed method to track file downloads
  const trackFileDownloadFn: TrackFileDownloadFn = useCallback(
    (name, type) => {
      dataLayer.push({ event: 'downloadDocument', documentInfo: { name, type } });
    },
    [dataLayer],
  );

  const trackPrintFn = useCallback(() => {
    dataLayer.push({
      event: 'downloadDocument',
      documentInfo: { name: 'print', type: 'pdf' },
    });
  }, [dataLayer]);

  useEffect(() => {
    window.addEventListener('beforeprint', trackPrintFn);

    return () => {
      window.removeEventListener('beforeprint', trackPrintFn);
    };
  }, [trackPrintFn]);

  const eventTrackingInterface: EventTrackingInterface = useMemo(
    () => ({
      trackEvent: trackEventFn,
      trackEventAsync,
      trackPageView: trackPageViewFn,
      trackProductView: trackProductViewFn,
      trackBeginCheckout: trackBeginCheckoutFn,
      trackPurchase: trackPurchaseFn,
      trackLearningPurchase: trackLearningPurchaseFn,
      trackFileDownload: trackFileDownloadFn,
      trackPrint: trackPrintFn,
      trackVirtualPageView: trackVirtualPageViewFn,
    }),
    [
      trackEventFn,
      trackEventAsync,
      trackPageViewFn,
      trackFileDownloadFn,
      trackProductViewFn,
      trackBeginCheckoutFn,
      trackPurchaseFn,
      trackLearningPurchaseFn,
      trackPrintFn,
      trackVirtualPageViewFn,
    ],
  );

  return <EventTracking value={eventTrackingInterface}>{children}</EventTracking>;
};

const useEventTracking = (): EventTrackingInterface => use(EventTracking);

export { EventTrackingProvider, useEventTracking };
